import { useCallback, useEffect, useState } from 'react';
import {
  CHART_SETTINGS_STORAGE_KEY,
  chartSettingsDefaults,
  ChartSettings,
  ChartSettingsEnvelope,
  validateAndNormalizeChartSettings,
} from '../config/chartSettingsDefaults';

type UseChartSettingsTuple = [ChartSettings, (partial: Partial<ChartSettings>) => void, () => void];

export default function useChartSettings(): UseChartSettingsTuple {
  const readFromStorage = (): ChartSettings => {
    if (typeof window === 'undefined') return chartSettingsDefaults;
    try {
      const raw = localStorage.getItem(CHART_SETTINGS_STORAGE_KEY);
      if (!raw) return chartSettingsDefaults;
      const parsed = JSON.parse(raw);
      const env = validateAndNormalizeChartSettings(parsed);
      return env.settings;
    } catch {
      return chartSettingsDefaults;
    }
  };

  const [settings, setSettings] = useState<ChartSettings>(() => readFromStorage());

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== CHART_SETTINGS_STORAGE_KEY) return;
      try {
        if (!e.newValue) {
          setSettings(chartSettingsDefaults);
          return;
        }
        const parsed = JSON.parse(e.newValue);
        const env = validateAndNormalizeChartSettings(parsed);
        setSettings(env.settings);
      } catch {
        setSettings(chartSettingsDefaults);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setChartSettings = useCallback((partial: Partial<ChartSettings>) => {
    setSettings((prev) => {
      const next = { ...prev, ...partial };
      const envelope: ChartSettingsEnvelope = { version: 1, updatedAt: Date.now(), settings: next };
      try { localStorage.setItem(CHART_SETTINGS_STORAGE_KEY, JSON.stringify(envelope)); } catch {}
      return next;
    });
  }, []);

  const resetChartSettings = useCallback(() => {
    try {
      const envelope: ChartSettingsEnvelope = { version: 1, updatedAt: Date.now(), settings: chartSettingsDefaults };
      localStorage.setItem(CHART_SETTINGS_STORAGE_KEY, JSON.stringify(envelope));
    } catch {}
    setSettings(chartSettingsDefaults);
  }, []);

  return [settings, setChartSettings, resetChartSettings];
}
